import { Component, inject, OnInit, NgZone, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet, Router, NavigationEnd, NavigationStart } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { ThirdPartyScriptsService } from '@shared/services/third-party-scripts.service';
import { InactivityService } from '@shared/services/inactivity.service';
import { GtmService } from '@shared/services/gtm.service';
import { environment } from '@environments';
import { LoaderComponent } from '@shared/components/ui/loader/loader.component';
import { ErrorModalComponent } from '@shared/components/ui/error-modal/error-modal.component';
import { ViewportGuardComponent } from '@shared/components/ui/viewport-guard/viewport-guard.component';
import { routeFadeAnimation } from '@shared/animations/route.animation';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, LoaderComponent, ErrorModalComponent, ViewportGuardComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  animations: [routeFadeAnimation]
})
export class App implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly thirdPartyScripts = inject(ThirdPartyScriptsService);
  private readonly gtm = inject(GtmService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly inactivity = inject(InactivityService);

  private currentHref = location.href;
  private currentUrl = '';

  prepareRoute(outlet: RouterOutlet): string {
    return outlet.isActivated ? (outlet.activatedRoute.snapshot.routeConfig?.path ?? '') : '';
  }

  ngOnInit(): void {
    this.translate.setDefaultLang('es');
    this.translate.use('es');
    this.gtm.initialize();

    if (environment.dynatraceUrl) {
      this.thirdPartyScripts.loadDynatrace(environment.dynatraceUrl);
    }
  }

  constructor() {
    this.currentUrl = this.router.url;
    this.blockBrowserNavigation();

    this.router.events.pipe(
      filter((event): event is NavigationStart => event instanceof NavigationStart),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event) => {
      if (event.navigationTrigger === 'popstate') {
        this.router.navigateByUrl(this.currentUrl, { replaceUrl: true });
        return;
      }
      window.scrollTo({ top: 0, behavior: 'instant' });
    });

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event) => {
      this.currentUrl = event.urlAfterRedirects;
      this.currentHref = location.href;
      this.updateInactivityMonitor(event.urlAfterRedirects);
    });
  }

  private updateInactivityMonitor(url: string): void {
    const isOnboarding = url.includes('onboarding/') && !url.includes('inactivity');
    if (isOnboarding) {
      this.inactivity.start();
    } else {
      this.inactivity.stop();
    }
  }

  private blockBrowserNavigation(): void {
    this.ngZone.runOutsideAngular(() => {
      const onPopState = (): void => {
        history.pushState(null, '', this.currentHref);
      };

      history.pushState(null, '', location.href);
      window.addEventListener('popstate', onPopState);

      this.destroyRef.onDestroy(() => {
        window.removeEventListener('popstate', onPopState);
      });
    });
  }
}
